import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { et, enGB } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore, usePMStore } from "@/stores/pm/hooks/usePMStore";
import * as pmRepo from "@/stores/pm/repository";
import type { PMIssue } from "@/domains/pm/types";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { isIssueUnscheduled } from "@/domains/pm/utils/issueDates";
import { groupAndSortIssues, type IssueGroupBy } from "@/domains/pm/utils/groupAndSort";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { useScheduleChangeApi } from "@/domains/pm/components/issues/schedule/scheduleChangeContext";
import { useIssueDependencies } from "@/domains/pm/hooks/useIssueDependencies";
import { useScheduleStaging } from "@/domains/pm/components/issues/schedule/scheduleStagingContext";
import { defaultsForGroup, type GanttGroupDefaults } from "@/domains/pm/utils/ganttGroupDefaults";
import {
  bandOrder,
  buildGanttLayout,
  draggedSlots,
  dragSourceAt,
  firstRowTops,
  previewDrop,
  rowDropTarget,
  type RowDragSource,
  type RowDropTarget,
} from "@/domains/pm/utils/ganttRowDrag";
import { arrangeGroups } from "@/domains/pm/utils/issuePlacement";
import { useIssuePlacements } from "@/domains/pm/hooks/useIssuePlacements";
import { usePermissions } from "@/hooks/usePermissions";
import { SnapshotControls } from "../snapshots/SnapshotControls";
import { snapshotIssuesAsPMIssues } from "@/domains/pm/utils/scheduleSnapshotDiff";
import { getScheduleSnapshot } from "@/stores/pm/repository";
import type { ScheduleSnapshot } from "@/domains/pm/types";
import {
  isEdgeStaged,
  isIssueStaged,
  overlayDependencies,
  overlayIssues,
} from "@/domains/pm/hooks/useStagedSchedule";
import { GanttConfirmBar } from "./GanttConfirmBar";
import { useLinkGesture } from "./useLinkGesture";
import { GanttLinkPreview } from "./GanttLinkPreview";
import { useEdgeAutoScroll } from "./useEdgeAutoScroll";
import { GanttDependencyLayer } from "./GanttDependencyLayer";
import {
  resolveStatus,
  statusLabelMap,
  visibleStatusKeys,
} from "@/domains/pm/statuses/statusCatalogue";
import {
  buildScale,
  computeDateRange,
  daysToFill,
  scaleWidth,
  todayX,
  type GanttGranularity,
} from "@/domains/pm/utils/ganttScale";
import { applyDrag, type GanttDragMode } from "@/domains/pm/utils/ganttDrag";
import { GanttHeader } from "./GanttHeader";
import { GanttGroupRow } from "./GanttGroupRow";
import { GanttDraftRow, GanttRow } from "./GanttRow";
import { isDraftIssue, isDraftIssueId } from "@/domains/pm/issueDraft";
import { useDraftIssueView, type DraftIssueViewProps } from "../issueDraftView";
import { GanttGranularityControl } from "./GanttGranularityControl";
import {
  GANTT_GROUP_ROW_HEIGHT,
  GANTT_HEADER_HEIGHT,
  GANTT_LABEL_MAX_WIDTH,
  GANTT_LABEL_MIN_WIDTH,
  GANTT_LABEL_WIDTH,
  GANTT_ROW_HEIGHT,
} from "./ganttConstants";
import { useGanttViewport } from "./useGanttViewport";
import { useAnimatedRowTops } from "./useAnimatedRowTops";
import { stickyChromeClipPath, useStickyChromeClip } from "./useStickyChromeClip";
import { useResizablePanelWidth } from "@/hooks/useResizablePanelWidth";
import { useFillToBottomHeight } from "@/hooks/useFillToBottomHeight";
import { useStableCallback } from "@/hooks/useStableCallback";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";

interface GanttViewProps extends DraftIssueViewProps {
  buildingId: number;
  groupBy: IssueGroupBy;
  /** The project in scope, or null for "All projects" — decides which custom
   *  statuses group and how a status key resolves. */
  buildingProjectId?: number | null;
  selectedIssueId?: number;
  onSelectIssue: (issue: PMIssue) => void;
  /** Set while a bar is being dragged; the page suppresses its realtime
   *  reload for the duration. Omitted by a read-only pane, which has none. */
  onDragActiveChange?: (active: boolean) => void;
  /** Opens the New Issue form, prefilled from the band it was clicked in.
   *  Omitted when the user cannot create issues here. */
  onAddIssue?: (defaults: GanttGroupDefaults) => void;
  /**
   * A snapshot to render instead of the live schedule. Owned by the PAGE:
   * "Compare with today" sits in the view-tabs row, above this component, and
   * the compare modal mounts two of these against one snapshot.
   */
  snapshot?: ScheduleSnapshot | null;
  onSnapshotChange?: (next: ScheduleSnapshot | null) => void;
  /**
   * Force read-only even on the live schedule.
   *
   * Deliberately separate from `snapshot`: WHICH schedule is drawn and whether
   * it can be edited are two different questions, and conflating them once
   * left a read-only view of today's schedule rendering nothing at all.
   */
  readOnly?: boolean;
}

export const GanttView = observer(function GanttView({
  buildingId,
  groupBy,
  buildingProjectId,
  selectedIssueId,
  onSelectIssue,
  onDragActiveChange,
  onAddIssue,
  snapshot = null,
  onSnapshotChange,
  readOnly: forcedReadOnly = false,
  ...draftProps
}: GanttViewProps) {
  const { t, language } = useLanguage();
  const { catalogue: statusCatalogue } = useStatusCatalogue();
  const schedule = useScheduleChangeApi();
  const { dependencies } = useIssueDependencies(buildingId);
  const staging = useScheduleStaging();
  const { canManagePMOnProject } = usePermissions();
  // Dragging a bar or drawing a dependency needs MANAGE_PM on the issue's
  // project. Refused with a message at the moment it would be written, so the
  // bar snaps home with an explanation instead of failing at RLS.
  const denyPermission = usePermissionDenied();
  const mayEditIssue = (issue: PMIssue) => {
    if (canManagePMOnProject(issue.building_project_id)) return true;
    void denyPermission("manageIssues", issue.building_project_id);
    return false;
  };
  /**
   * Whether this user may store a position for one issue. A band's stored
   * order reaches issues that are not on the chart — hidden by a filter, or
   * moved to another band since — and RLS refuses the write whole, so an id
   * nothing here accounts for is not written. Through a ref because the chart's
   * issues are derived below, after the arrangement they feed.
   */
  const issuesByIdRef = useRef<Map<number, PMIssue>>(new Map());
  const mayArrangeIssue = (issueId: number) => {
    // A draft has no row to place; its id must never reach a placement write.
    if (isDraftIssueId(issueId)) return false;
    const issue = issuesByIdRef.current.get(issueId);
    return !!issue && canManagePMOnProject(issue.building_project_id);
  };
  /** The project's shared arrangement of the chart, under this grouping. */
  const placements = useIssuePlacements(buildingId, "gantt", groupBy, mayArrangeIssue);
  /**
   * A vertical drag in progress. Mirrored in a ref because the auto-scroll
   * loop and the pointer handlers must read the latest value, not the one their
   * render closed over.
   */
  const [rowDrag, setRowDragState] = useState<RowDrag | null>(null);
  const rowDragRef = useRef<RowDrag | null>(null);
  const setRowDrag = useCallback((next: RowDrag | null) => {
    rowDragRef.current = next;
    setRowDragState(next);
  }, []);
  /**
   * A drop whose new order is still on its way into the cache. Previewed until
   * the arrangement catches up, so the rows glide into place once instead of
   * snapping back to where they were for the length of a round trip.
   */
  const [settling, setSettling] = useState<Settling | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // The label column. Left-anchored, so its handle is on the RIGHT edge and
  // dragging right widens it — the mirror of every other caller of this hook.
  const labelResize = useResizablePanelWidth("gantt-label-column:v1", GANTT_LABEL_WIDTH, {
    minWidth: GANTT_LABEL_MIN_WIDTH,
    maxWidth: GANTT_LABEL_MAX_WIDTH,
    anchor: "left",
  });
  const labelWidth = labelResize.width;
  const issuesStore = useIssuesStore();
  const pmStore = usePMStore();
  const [granularity, setGranularity] = useState<GanttGranularity>("week");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const centredFor = useRef<string | null>(null);
  // How much chart there is to fill. The axis and the grid are both sized
  // from this rather than from the data — see `useGanttViewport`.
  const viewportWidth = useGanttViewport(scrollRef);
  useStickyChromeClip(scrollRef);
  // The chart owns its own scrolling, so it has to be told how tall it may be:
  // its host is an ordinary page scroller, where `flex-1` resolves to nothing
  // and the chart is simply as tall as its rows. This measures the distance
  // down to the bottom of the building shell's column, less the chat pill's
  // spacer, which is the last thing after it.
  const fill = useFillToBottomHeight();

  // The staged arrangement is drawn as an OVERLAY over the store, never by
  // mutating it: a realtime reload replaces these objects wholesale, and a
  // buffer keyed by issue id survives that where edited copies would not.
  const liveIssues = useMemo(
    () => (staging ? overlayIssues(issuesStore.issues, staging.staged) : issuesStore.issues),
    [issuesStore.issues, staging]
  );
  const snapshotIssues = useMemo(
    () =>
      snapshot?.payload && buildingProjectId != null
        ? snapshotIssuesAsPMIssues(
            snapshot.payload,
            {
              building_id: buildingId,
              org_id: issuesStore.issues[0]?.org_id ?? "",
              building_project_id: buildingProjectId,
            },
            pmStore.disciplines
          )
        : [],
    [snapshot, buildingId, buildingProjectId, issuesStore.issues, pmStore.disciplines]
  );
  // WHICH schedule is drawn, and whether it can be edited, are two separate
  // questions — a compare pane renders the LIVE schedule read-only. Deriving
  // the source from `readOnly` would leave that pane with no issues at all.
  const showingSnapshot = snapshot != null;
  // A snapshot is a record, so viewing one is read-only: no drags, no
  // connectors, no Confirm. A compare pane is read-only for its own reason.
  const readOnly = showingSnapshot || forcedReadOnly;
  const issues = showingSnapshot ? snapshotIssues : liveIssues;
  // New issues still being written. Drawn in their own rows above the bands,
  // never mixed into `issues`: everything below — the grouping, the layout a
  // drag is measured against, the band order a drop stores, the staging
  // overlay, the arrows — then cannot see a draft id. A snapshot is a record
  // of the past, so it shows none.
  const draftView = useDraftIssueView(draftProps);
  const drafts = showingSnapshot ? NO_DRAFTS : draftView.drafts;
  const draftsHeight = drafts.length * GANTT_ROW_HEIGHT;
  // The drafts' dates, as a key: the page hands a fresh drafts array on every
  // render, and a new scale would redraw every row.
  const draftDates = drafts
    .flatMap((d) => [d.start_date, d.due_date])
    .filter((d): d is string => !!d)
    .join(",");

  const shownDependencies = useMemo(() => {
    if (showingSnapshot) {
      // The snapshot's own graph, keyed by index — these rows have no ids of
      // their own and nothing here writes them.
      return (snapshot?.payload?.dependencies ?? []).map((d, index) => ({ ...d, id: -1 - index }));
    }
    return staging ? overlayDependencies(dependencies.all, staging.staged) : dependencies.all;
  }, [dependencies, staging, showingSnapshot, snapshot]);
  // Only the DATED issues set the axis; an undated one contributes no date and
  // so cannot stretch it. Every issue is grouped, though — an issue with no
  // dates gets a row in its own band with an empty timeline, which reads as
  // "not scheduled yet" where a separate list below the chart read as a
  // footnote.
  const scheduled = useMemo(() => issues.filter((i) => !isIssueUnscheduled(i)), [issues]);

  const scale = useMemo(() => {
    const dates: string[] = [];
    for (const i of scheduled) {
      if (i.start_date) dates.push(i.start_date);
      if (i.due_date) dates.push(i.due_date);
    }
    // A draft's dates stretch the axis too, so its bar is never off the end.
    if (draftDates) dates.push(...draftDates.split(","));
    // The axis runs to the right-hand edge whatever the data says.
    const minDays = daysToFill(viewportWidth - labelWidth, granularity);
    return buildScale(computeDateRange(dates, new Date(), minDays), granularity);
  }, [scheduled, draftDates, granularity, viewportWidth, labelWidth]);

  // Rows come from the SAME grouping the list uses, so a subissue sits under
  // its parent here exactly as it does there.
  const sortedGroups = useMemo(
    () =>
      groupAndSortIssues({
        issues,
        allIssues: issues,
        groupBy,
        // Not by date: ordering by date meant a bar jumped to a different row
        // the moment you moved it, which reads as the chart rearranging itself
        // under your hand. Issue number is only the fallback — the shared
        // arrangement below decides.
        sort: { field: "issue_number", direction: "asc" },
        dateLabels: t.pm.issues.dateBuckets,
        statusLabels: statusLabelMap(
          statusCatalogue,
          buildingProjectId,
          issues.map((i) => i.status)
        ),
        statusOrder: visibleStatusKeys(statusCatalogue, buildingProjectId),
        noDisciplineLabel: t.pm.issues.noDiscipline,
        noAssigneeLabel: t.pm.issues.noAssignee,
        language,
      }),
    [issues, groupBy, t, language, statusCatalogue, buildingProjectId]
  );

  // Manual placement first, then dependency partners pulled together so an
  // arrow crosses as few rows as it can (see issuePlacement.ts).
  const arrangedGroups = useMemo(
    () => arrangeGroups(sortedGroups, placements.ranksFor, shownDependencies),
    [sortedGroups, placements.ranksFor, shownDependencies]
  );

  /**
   * Where every row sits inside the chart body, by arithmetic rather than by
   * measuring the DOM (which would have to re-measure on every scroll,
   * granularity change and collapse).
   *
   * Two layouts. `baseLayout` is the arrangement as it stands and is what a
   * drag is measured against; `layout` is what is drawn — the same, or with
   * the dragged block already moved to where it would land, so its neighbours
   * make room under the pointer.
   */
  const baseLayout = useMemo(
    () => buildGanttLayout(arrangedGroups, collapsed, GANTT_GROUP_ROW_HEIGHT, GANTT_ROW_HEIGHT),
    [arrangedGroups, collapsed]
  );
  const baseLayoutRef = useRef(baseLayout);
  baseLayoutRef.current = baseLayout;

  const previewing = rowDrag?.target ? rowDrag : settling;
  const groups = useMemo(
    () =>
      previewing?.target
        ? previewDrop(arrangedGroups, previewing.source, previewing.target)
        : arrangedGroups,
    [arrangedGroups, previewing]
  );
  const layout = useMemo(
    () => buildGanttLayout(groups, collapsed, GANTT_GROUP_ROW_HEIGHT, GANTT_ROW_HEIGHT),
    [groups, collapsed]
  );

  // The drop has landed once the arrangement reads the way it was dropped.
  useEffect(() => {
    if (!settling) return;
    const now = bandOrder(arrangedGroups, settling.source, settling.target.groupKey);
    if (sameIds(now, settling.order)) setSettling(null);
  }, [arrangedGroups, settling]);

  /** The rows under the pointer, keyed as the renderer keys them. */
  const liftedRows = useMemo(() => {
    const lifted = new Map<string, number>();
    if (!rowDrag) return lifted;
    const band = rowDrag.target?.groupKey ?? rowDrag.source.groupKey;
    const slots = draggedSlots(layout, rowDrag.source, band);
    const headTop = slots[0]?.top ?? 0;
    for (const slot of slots) {
      lifted.set(`${slot.groupKey}-${slot.issueId}`, rowDrag.blockTop + slot.top - headTop);
    }
    return lifted;
  }, [layout, rowDrag]);

  const slotTops = useMemo(
    () => new Map(layout.slots.map((slot) => [`${slot.groupKey}-${slot.issueId}`, slot.top])),
    [layout]
  );
  const rowTop = (groupKey: string, issueId: number) => {
    const key = `${groupKey}-${issueId}`;
    return liftedRows.get(key) ?? slotTops.get(key) ?? 0;
  };

  /**
   * Where the arrows attach. An issue grouped by a multi-value dimension
   * appears in several groups; the arrows use its FIRST row. Drawing one arrow
   * per copy would multiply a single dependency into a thicket that says
   * nothing extra.
   */
  const { arrowTargets, pointerDriven } = useMemo(() => {
    const targets = firstRowTops(layout);
    const driven = new Set<number>();
    for (const slot of layout.slots) {
      const lifted = liftedRows.get(`${slot.groupKey}-${slot.issueId}`);
      if (lifted !== undefined && targets.get(slot.issueId) === slot.top) {
        targets.set(slot.issueId, lifted);
        driven.add(slot.issueId);
      }
    }
    return { arrowTargets: targets, pointerDriven: driven };
  }, [layout, liftedRows]);
  const rowTops = useAnimatedRowTops(arrowTargets, pointerDriven, groupBy);

  const bodyHeight = layout.height;

  const issuesById = useMemo(
    () => new Map<number, PMIssue>(issues.map((i) => [i.id, i])),
    [issues]
  );
  issuesByIdRef.current = issuesById;

  /**
   * Bands this user may rearrange. A drop stores the order of the WHOLE band,
   * and "All projects" can put several projects' issues in one — so it takes
   * MANAGE_PM on every one of them, or the save would be refused after the
   * rows had already moved.
   */
  const arrangeableBands = useMemo(() => {
    const bands = new Set<string>();
    if (readOnly) return bands;
    for (const group of arrangedGroups) {
      const projects = new Set(group.items.map((item) => item.issue.building_project_id));
      if ([...projects].every((projectId) => canManagePMOnProject(projectId))) {
        bands.add(group.key);
      }
    }
    return bands;
  }, [arrangedGroups, canManagePMOnProject, readOnly]);

  const link = useLinkGesture({
    buildingId,
    scrollRef,
    labelWidth,
    dependencies: shownDependencies,
    issuesById,
    onDragActiveChange,
  });

  // Open with today about a quarter in, so there is visible past and future.
  // Re-centres when the axis is rebuilt by a granularity change, not on every
  // data tick — otherwise scrolling would fight the user during a drag.
  useEffect(() => {
    const key = `${granularity}:${scale.origin}:${scale.days}`;
    if (centredFor.current === key) return;
    const el = scrollRef.current;
    if (!el) return;
    centredFor.current = key;
    el.scrollLeft = Math.max(0, todayX(scale) - el.clientWidth / 4);
  }, [granularity, scale]);

  /** A client y in the chart body's own coordinates — scroll included, since
   *  the body's rect moves with it. */
  const toBodyY = (clientY: number): number | null => {
    const body = bodyRef.current;
    return body ? clientY - body.getBoundingClientRect().top : null;
  };

  /**
   * A band is droppable only when its key stands for a value an issue can be
   * given. `defaultsForGroup` already answers that — it returns nothing for a
   * date bucket (a range is not a value) or for a "no discipline" band.
   */
  const canDropInto = (groupKey: string): boolean => {
    if ((groupBy !== "status" && groupBy !== "assignee") || !arrangeableBands.has(groupKey)) {
      return false;
    }
    const defaults = defaultsForGroup(groupBy, groupKey);
    return defaults.status !== undefined || defaults.assigneeIds !== undefined;
  };

  /** Re-resolve the drag after the pointer or the scroll position moved. */
  const followPointer = (current: RowDrag, clientY: number) => {
    const bodyY = toBodyY(clientY);
    if (bodyY == null) return;
    const blockTop = bodyY - current.grabOffset;
    const target = rowDropTarget(
      baseLayoutRef.current,
      current.source,
      blockTop,
      GANTT_ROW_HEIGHT,
      canDropInto
    );
    setRowDrag({ ...current, clientY, blockTop, target });
  };

  /**
   * Scroll while a dragged row is held near the top or bottom edge, faster the
   * closer it is, so a row can be carried past what fits on screen. The
   * pointer is captured by the bar and sends no events while it holds still,
   * so the target is re-resolved after each step.
   */
  const rowAutoScroll = useEdgeAutoScroll(scrollRef);
  const stopAutoScroll = rowAutoScroll.stop;
  const startAutoScroll = () =>
    rowAutoScroll.start({
      pointer: () => {
        const current = rowDragRef.current;
        return current ? { x: 0, y: current.clientY } : null;
      },
      // The axis header is sticky, so the usable top edge is below it.
      insetTop: GANTT_HEADER_HEIGHT,
      onScrolled: () => {
        const current = rowDragRef.current;
        if (current) liveDrag.current.followPointer(current, current.clientY);
      },
    });

  /** Abandoned or cancelled: the rows glide back, nothing is written. */
  const abortRowDrag = () => {
    stopAutoScroll();
    setRowDrag(null);
  };

  const verticalMove = (issue: PMIssue, clientY: number | null, groupKey: string) => {
    if (clientY == null) {
      abortRowDrag();
      return;
    }
    const current = rowDragRef.current;
    if (current) {
      followPointer(current, clientY);
      return;
    }
    const source = dragSourceAt(baseLayout, groupKey, issue.id);
    const bodyY = toBodyY(clientY);
    const head = source ? draggedSlots(baseLayout, source, source.groupKey)[0] : undefined;
    if (!source || !head || bodyY == null) return;
    setSettling(null);
    followPointer(
      { source, grabOffset: bodyY - head.top, clientY, blockTop: head.top, target: null },
      clientY
    );
    startAutoScroll();
  };

  const dropRowAt = (clientY: number) => {
    stopAutoScroll();
    const current = rowDragRef.current;
    setRowDrag(null);
    if (!current) return;
    const bodyY = toBodyY(clientY);
    const blockTop = bodyY == null ? current.blockTop : bodyY - current.grabOffset;
    const target = rowDropTarget(
      baseLayout,
      current.source,
      blockTop,
      GANTT_ROW_HEIGHT,
      canDropInto
    );
    if (!target) return;

    const { source } = current;
    const order = bandOrder(previewDrop(arrangedGroups, source, target), source, target.groupKey);
    if (
      target.groupKey === source.groupKey &&
      sameIds(order, bandOrder(arrangedGroups, source, source.groupKey))
    ) {
      return;
    }

    if (target.groupKey !== source.groupKey) {
      const moved = issuesById.get(source.unitId);
      if (!moved || !applyBandChange(moved, target.groupKey)) return;
    }

    // Dropping into another band still says where in that band the issue
    // belongs, so the band's order is stored either way.
    const landed: Settling = { source, target, order };
    setSettling(landed);
    void placements
      .saveBand(target.groupKey, order)
      .finally(() => setSettling((pending) => (pending === landed ? null : pending)));
  };

  /**
   * A live vertical drag is followed from the WINDOW, not from the bar that
   * started it.
   *
   * The preview re-parents the dragged row into the band it would land in, so
   * crossing a band boundary unmounts the bar holding pointer capture and
   * mounts a fresh one with no drag state. The `pointerup` that ends the
   * gesture then reaches no handler at all: the drop would never run, the rows
   * would stay lifted under a pointer nobody is pressing, and the auto-scroll
   * loop would keep stepping. The bar still STARTS the gesture — it is what
   * knows the axis — and its own handlers stay in place for the common case;
   * both paths land in the same place and the second is a no-op, because the
   * drag is read from `rowDragRef` and cleared before anything is written.
   */
  const dragging = rowDrag !== null;
  const liveDrag = useRef({ followPointer, dropRowAt, abortRowDrag });
  liveDrag.current = { followPointer, dropRowAt, abortRowDrag };
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const current = rowDragRef.current;
      if (current) liveDrag.current.followPointer(current, e.clientY);
    };
    const up = (e: PointerEvent) => liveDrag.current.dropRowAt(e.clientY);
    const cancel = () => liveDrag.current.abortRowDrag();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [dragging]);

  /**
   * Dropping into another band sets that band's value. Written immediately,
   * not staged: status and assignee changes save immediately on every other
   * surface, and Confirm is about dates and dependencies.
   */
  const applyBandChange = (issue: PMIssue, groupKey: string): boolean => {
    const defaults = defaultsForGroup(groupBy, groupKey);

    if (defaults.status !== undefined) {
      // The Kanban's guard: a custom status belongs to ONE project and this
      // chart can show several, so refuse it here where we can say why rather
      // than letting the database trigger reject the write.
      const target = resolveStatus(statusCatalogue, issue.building_project_id, defaults.status);
      if (target.custom && target.buildingProjectId !== issue.building_project_id) {
        toast.error(t.pm.issues.statuses.notInThisProject);
        return false;
      }
      issuesStore.updateIssueOptimistic(issue.id, { status: defaults.status });
      issuesStore.updateIssue(issue.id, { status: defaults.status }).catch((e) => {
        toast.error(friendlyIssueError(e, t));
        void issuesStore.loadIssues(buildingId);
      });
      return true;
    }

    if (defaults.assigneeIds !== undefined) {
      const userId = defaults.assigneeIds[0];
      const band = issuesStore.issues
        .flatMap((i) => i.assignees ?? [])
        .find((u) => u.id === userId);
      // Optimistic from the band's own user object: projectMembers is only
      // warm once a picker has been opened, and from a drag it may be cold.
      issuesStore.updateIssueOptimistic(issue.id, { assignees: band ? [band] : [] });
      void pmRepo
        .setIssueAssignees(issue.id, userId != null ? [userId] : [])
        .then(() => issuesStore.reconcileJunctionWrite("assignees"))
        .catch((e) => {
          toast.error(friendlyIssueError(e, t));
          void issuesStore.loadIssues(buildingId);
        });
      return true;
    }
    return false;
  };

  const commit = (issue: PMIssue, mode: GanttDragMode, dxPx: number) => {
    const patch = applyDrag(scale, { start: issue.start_date, due: issue.due_date }, mode, dxPx);
    if (Object.keys(patch).length === 0) return;
    if (!mayEditIssue(issue)) return;
    if (schedule) {
      // The same path the list cells and the context menu take, so dragging a
      // bar and typing a date into the panel produce the same cascade.
      void schedule.request(issue, patch);
      return;
    }
    // No provider (a component rendered on its own): write the bar's own dates
    // and say so if it fails. A bar that silently snaps home is unexplainable.
    issuesStore.updateIssueOptimistic(issue.id, patch);
    issuesStore.updateIssue(issue.id, patch).catch((e) => {
      toast.error(friendlyIssueError(e, t));
      issuesStore.loadIssues(buildingId);
    });
  };

  const locale = language === "et" ? et : enGB;
  const barLabel = (issue: PMIssue) => {
    const fmt = (d: string) => format(parseISO(d), "d MMM yyyy", { locale });
    // Only scheduled issues reach a bar, so at least one date is set — but
    // narrow rather than assert, so a future caller cannot make this lie.
    const single = issue.start_date ?? issue.due_date;
    const span =
      issue.start_date && issue.due_date
        ? `${fmt(issue.start_date)} – ${fmt(issue.due_date)}`
        : single
          ? fmt(single)
          : "";
    const name = isDraftIssue(issue)
      ? `${t.pm.issues.draft.marker} ${issue.title.trim() || t.pm.issues.draft.untitled}`
      : `#${issue.issue_number} ${issue.title}`;
    return `${name}, ${span}`.trim();
  };

  // The rows are memoised, so what they are handed must keep its identity
  // across renders — see useStableCallback.
  const rowSelect = useStableCallback(onSelectIssue);
  const rowCommit = useStableCallback(commit);
  const rowLinkStart = useStableCallback(
    (issue: PMIssue, side: Parameters<typeof link.begin>[1], e: React.PointerEvent) => {
      if (!mayEditIssue(issue)) return;
      link.begin(issue, side, e);
    }
  );
  const rowDragActive = useStableCallback((active: boolean) => onDragActiveChange?.(active));
  const rowBarLabel = useStableCallback(barLabel);
  const rowVerticalMove = useStableCallback(verticalMove);
  const rowVerticalDrop = useStableCallback((_issue: PMIssue, clientY: number) =>
    dropRowAt(clientY)
  );
  const selectDraft = useStableCallback(draftView.select);

  const width = scaleWidth(scale);
  const marker = todayX(scale);
  // The page caps the load; on a timeline a missing bar reads as "unscheduled"
  // rather than "not loaded", so the gap has to be stated rather than implied.
  // The store now pages the whole scope in the background and only stops at
  // PM_ISSUE_LOAD_CEILING, so "not everything is here" is its own flag rather
  // than a page-size comparison.
  const truncated = issuesStore.truncated;

  return (
    <div
      ref={fill.measureRef}
      className="flex min-h-0 flex-1 flex-col"
      style={{ height: fill.maxHeight }}
    >
      {/* No border-t: the page's view-tab rule directly above is the top edge. */}
      <div ref={scrollRef} className="relative flex-1 overflow-auto">
        {/* `min-h-full` rather than a measured height: the header takes its own
            height and the body takes exactly what is left, so the content is
            the height of the box to the pixel and no vertical scrollbar is
            summoned by the fill itself. Subtracting a header constant got this
            wrong by the 1px of its bottom border, which was enough. */}
        <div className="flex min-h-full flex-col" style={{ width: labelWidth + width }}>
          <GanttHeader
            scale={scale}
            labelWidth={labelWidth}
            controls={
              <>
                <GanttGranularityControl value={granularity} onChange={setGranularity} />
                {buildingProjectId != null && !readOnly && onSnapshotChange && (
                  <SnapshotControls
                    buildingProjectId={buildingProjectId}
                    hasUnsavedChanges={staging?.staged.isDirty ?? false}
                    canManage={canManagePMOnProject(buildingProjectId)}
                    onOpenSnapshot={(chosen) => {
                      void getScheduleSnapshot(chosen.id)
                        .then((full) => onSnapshotChange(full))
                        .catch((e) => toast.error(friendlyIssueError(e, t)));
                    }}
                  />
                )}
              </>
            }
            resizeHandle={
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t.pm.issues.gantt.resizeColumn}
                {...labelResize.handleProps}
                className="absolute bottom-0 right-0 top-0 z-40 w-1.5 cursor-col-resize touch-none transition-colors"
                style={{
                  background:
                    labelResize.dragging || labelResize.hover
                      ? "hsl(var(--primary) / 0.4)"
                      : "transparent",
                }}
              />
            }
          />

          <div className="relative flex flex-1 flex-col">
            {/* Background: weekends, then the today line. Both are decoration
              and must never intercept a drag. Clipped like the arrows: the
              group bands' title cells are translucent, and the line showed
              through them (and between rows) once scrolled sideways. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 z-0"
              style={{ left: labelWidth, width, clipPath: stickyChromeClipPath(0) }}
            >
              <div className="absolute inset-y-0 w-0.5 bg-[#ef4129]" style={{ left: marker }} />
            </div>

            {/* Arrows sit above the rows but take no pointer except on their own
              strokes, so a bar underneath is still draggable. They are drawn
              in the body's coordinates, so they start below the draft rows,
              and clipped to the timeline that is showing: above the rows, they
              would otherwise cross the sticky titles and header. */}
            <div
              className="pointer-events-none absolute z-20"
              style={{
                top: draftsHeight,
                left: labelWidth,
                width,
                height: bodyHeight,
                clipPath: stickyChromeClipPath(draftsHeight),
              }}
              data-gantt-arrows
            >
              <GanttDependencyLayer
                dependencies={shownDependencies}
                issuesById={issuesById}
                rowTops={rowTops}
                scale={scale}
                width={width}
                height={bodyHeight}
                selectedId={link.selectedId}
                onSelect={link.select}
                isStaged={(id) => (staging ? isEdgeStaged(id, staging.staged) : false)}
              />
              {!readOnly && (
                <GanttLinkPreview preview={link.preview} width={width} height={bodyHeight} />
              )}
            </div>

            {drafts.length > 0 && (
              <div className="relative z-10 shrink-0" data-gantt-drafts>
                {drafts.map((draft) => (
                  <GanttDraftRow
                    key={draft.id}
                    issue={draft}
                    scale={scale}
                    active={draftView.isActive(draft.id)}
                    saving={draftView.isSaving(draft.id)}
                    onSelect={selectDraft}
                    barLabel={rowBarLabel}
                    labelWidth={labelWidth}
                  />
                ))}
              </div>
            )}

            {/* Rows are placed absolutely at their layout position, so a
              reorder, a collapse or a drop glides rather than jumps. */}
            <div ref={bodyRef} className="relative z-10 shrink-0" style={{ height: bodyHeight }}>
              {groups.map((group, groupIndex) => {
                const isCollapsed = collapsed.has(group.key);
                return (
                  <div key={group.key}>
                    <GanttGroupRow
                      top={layout.bands[groupIndex]?.top ?? 0}
                      label={group.label}
                      count={group.items.length}
                      collapsed={isCollapsed}
                      width={width}
                      labelWidth={labelWidth}
                      // Only a status band has a colour of its own; an assignee
                      // or a date bucket does not, and inventing one would say
                      // something the grouping does not mean.
                      dotColor={
                        groupBy === "status"
                          ? resolveStatus(statusCatalogue, buildingProjectId, group.key).dotColor
                          : undefined
                      }
                      addLabel={t.pm.issues.gantt.addIssueHere}
                      onAdd={
                        onAddIssue && !readOnly
                          ? () => onAddIssue(defaultsForGroup(groupBy, group.key))
                          : undefined
                      }
                      onToggle={() =>
                        setCollapsed((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.key)) next.delete(group.key);
                          else next.add(group.key);
                          return next;
                        })
                      }
                    />
                    {!isCollapsed &&
                      group.items.map((item) => {
                        const key = `${group.key}-${item.issue.id}`;
                        const mayArrange = arrangeableBands.has(group.key);
                        return (
                          <GanttRow
                            key={key}
                            issue={item.issue}
                            groupKey={group.key}
                            top={rowTop(group.key, item.issue.id)}
                            lifted={liftedRows.has(key)}
                            scale={scale}
                            indentLevel={item.indentLevel}
                            selected={selectedIssueId === item.issue.id}
                            onSelect={rowSelect}
                            onCommit={readOnly ? undefined : rowCommit}
                            onDragActiveChange={onDragActiveChange ? rowDragActive : undefined}
                            barLabel={rowBarLabel}
                            onLinkStart={readOnly ? undefined : rowLinkStart}
                            linking={!readOnly && link.active}
                            staged={staging ? isIssueStaged(item.issue.id, staging.staged) : false}
                            onVerticalMove={mayArrange ? rowVerticalMove : undefined}
                            onVerticalDrop={mayArrange ? rowVerticalDrop : undefined}
                            labelWidth={labelWidth}
                          />
                        );
                      })}
                  </div>
                );
              })}
            </div>

            {/* Below the last row the chart carries on: the today line already
              spans this box, and the label column's rule follows it down so
              the grid ends where the screen does rather than under the last
              bar. With nothing to draw at all it holds the empty message
              instead — a full-height column rule beside no rows would read as
              a chart that had failed to load. */}
            <div className="relative flex min-h-0 flex-1">
              {issues.length === 0 && drafts.length === 0 ? (
                <p className="sticky left-0 px-7 py-8 text-xs text-muted-foreground">
                  {t.pm.issues.gantt.emptyTimeline}
                </p>
              ) : (
                <div
                  aria-hidden="true"
                  className="sticky left-0 shrink-0 border-r bg-background"
                  style={{ width: labelWidth }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The cap notice sits OUTSIDE the horizontal scroller: it has nothing to
          say about the axis, and inside it would simply scroll away from the
          reader — `position: sticky` cannot rescue a row whose containing block
          is the full width of the chart. */}
      {truncated && (
        <p className="border-t px-7 py-3 text-[11px] text-muted-foreground">
          {t.pm.issues.gantt.truncated
            .replace("{shown}", String(issues.length))
            .replace("{total}", String(issuesStore.totalCount))}
        </p>
      )}
    </div>
  );
});

/** A vertical drag: what was picked up, where the pointer holds it, and where
 *  it would land (null over a band that cannot take it). */
interface RowDrag {
  source: RowDragSource;
  /** Pointer y minus the block's top when it was picked up. */
  grabOffset: number;
  clientY: number;
  blockTop: number;
  target: RowDropTarget | null;
}

interface Settling {
  source: RowDragSource;
  target: RowDropTarget;
  /** The band order the drop stored. */
  order: number[];
}

const NO_DRAFTS: readonly PMIssue[] = [];

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
